// Plans and payments on the page (RFC-065, byj-1wv.132.8): a price said in
// its currency, and the payment provider's window. Toss Payments' window
// (SDK v2) takes the card or wallet and comes back to /billing with what the
// server confirms; the fake provider (the mock's, and a development main
// server's) comes back at once as if paid. The amount the window is given is
// the server's order's, and the server checks it again.
import type { Checkout, Plan } from "./api";

/** A price in its currency, as the person reads it: ₩19,900. */
export function price(amount: number, currency: string): string {
  try {
    return new Intl.NumberFormat(undefined, { style: "currency", currency, maximumFractionDigits: 0 }).format(amount);
  } catch {
    return `${amount} ${currency}`;
  }
}

/** Where the window comes back to: /billing with the order and the step it was, and the provider's own parameters after. */
export function returnURLs(origin: string, c: Pick<Checkout, "order_id" | "kind">) {
  const step = c.kind === "card" ? "card" : "paid";
  return {
    successUrl: `${origin}/billing?order=${encodeURIComponent(c.order_id)}&step=${step}`,
    failUrl: `${origin}/billing?order=${encodeURIComponent(c.order_id)}&step=fail`,
  };
}

/** What the window brought back to /billing, read from its query; null when it brought nothing. */
export type Returned =
  | { step: "paid"; order: string; paymentKey: string; amount: number }
  | { step: "card"; order: string; authKey: string; customerKey: string }
  | { step: "fail"; order: string; code: string; message: string };

export function returned(q: URLSearchParams): Returned | null {
  const order = q.get("order") ?? "";
  switch (q.get("step")) {
    case "paid":
      return q.get("paymentKey") ? { step: "paid", order, paymentKey: q.get("paymentKey")!, amount: Number(q.get("amount")) } : null;
    case "card":
      return q.get("authKey") ? { step: "card", order, authKey: q.get("authKey")!, customerKey: q.get("customerKey") ?? "" } : null;
    case "fail":
      return { step: "fail", order, code: q.get("code") ?? "", message: q.get("message") ?? "" };
  }
  return null;
}

type TossPayment = {
  requestPayment(o: Record<string, unknown>): Promise<void>;
  requestBillingAuth(o: Record<string, unknown>): Promise<void>;
};
type TossPayments = ((clientKey: string) => { payment(o: { customerKey: string }): TossPayment }) & { ANONYMOUS: string };

const tossSDK = "https://js.tosspayments.com/v2/standard";

/** Toss Payments' SDK, loaded once. */
function loadToss(): Promise<TossPayments> {
  const w = window as unknown as { TossPayments?: TossPayments };
  if (w.TossPayments) return Promise.resolve(w.TossPayments);
  return new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = tossSDK;
    s.onload = () => (w.TossPayments ? resolve(w.TossPayments) : reject(new Error("the payment window did not load")));
    s.onerror = () => reject(new Error("the payment window did not load"));
    document.head.appendChild(s);
  });
}

/**
 * Opens the provider's window for an order: a card registered (its billing
 * auth) or a payment once. It leaves the page; the window comes back to
 * `returnURLs`. `go` is how the fake's comes back.
 */
export async function openWindow(c: Checkout, origin: string, go: (url: string) => void): Promise<void> {
  const urls = returnURLs(origin, c);
  if (c.provider === "fake") {
    const back =
      c.kind === "card"
        ? `${urls.successUrl}&customerKey=${encodeURIComponent(c.customer_key)}&authKey=auth_fake_${encodeURIComponent(c.order_id)}`
        : `${urls.successUrl}&paymentKey=pay_fake_${encodeURIComponent(c.order_id)}&orderId=${encodeURIComponent(c.order_id)}&amount=${c.amount}`;
    go(back.replace(origin, ""));
    return;
  }
  const toss = await loadToss();
  if (c.kind === "card") {
    await toss(c.client_key).payment({ customerKey: c.customer_key }).requestBillingAuth({ method: "CARD", ...urls });
    return;
  }
  await toss(c.client_key)
    .payment({ customerKey: toss.ANONYMOUS })
    .requestPayment({ method: "CARD", amount: { currency: c.currency, value: c.amount }, orderId: c.order_id, orderName: c.order_name, ...urls });
}

/** What a plan grants, as numbers the page words: machine hours a month, file storage in GB, a credit pack's hours. */
export function allowance(p: Plan): { hours?: number; gb?: number; creditHours?: number } {
  const out: { hours?: number; gb?: number; creditHours?: number } = {};
  for (const g of p.grants) {
    if (g.addon === "machines" && g.limits?.monthly_hours) out.hours = g.limits.monthly_hours;
    if (g.addon === "machines" && g.limits?.credit_hours) out.creditHours = g.limits.credit_hours;
    if (g.addon === "files" && g.limits?.quota_mb) out.gb = Math.round(g.limits.quota_mb / 1024);
  }
  return out;
}
